import React from 'react';
import { useListContext } from 'react-admin';
import { SlidersHorizontal, X } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './popover';
import { Checkbox } from './checkbox';
import { cn } from '../../lib/utils';

/**
 * Inline filters — DESIGN_SYSTEM.md §7/§8.
 * Per-field Popover of checkboxes wired to RA filterValues/setFilters.
 * Multi-select is OR within a field (array value), AND across fields (separate keys).
 * NOTE: array (multi) values require the dataProvider to serialize that field as
 * a list; single-value fields work out of the box. Per-section wiring is Phase 4.
 */
export type FilterOption = { value: string; label: string };
export type FilterFieldConfig = {
  /** RA filter key. */
  field: string;
  /** Button + token label. */
  label: string;
  options: FilterOption[];
  /** OR-within-field multi-select. Defaults to single-select. */
  multi?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
};

function asArray(value: unknown): string[] {
  if (Array.isArray(value)) return value as string[];
  if (value === undefined || value === null || value === '') return [];
  return [String(value)];
}

function omit<T extends Record<string, any>>(obj: T, key: string): T {
  const { [key]: _drop, ...rest } = obj;
  return rest as T;
}

function useFilterState() {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const setField = (field: string, selected: string[], multi?: boolean) => {
    let next = { ...filterValues };
    if (selected.length === 0) {
      next = omit(next, field);
    } else {
      next[field] = multi ? selected : selected[0];
    }
    setFilters(next, displayedFilters);
  };
  return { filterValues, setField, setFilters, displayedFilters };
}

export function FilterPopover({ config }: { config: FilterFieldConfig }) {
  const { filterValues, setField } = useFilterState();
  const selected = asArray(filterValues?.[config.field]);
  const Icon = config.icon ?? SlidersHorizontal;

  const toggle = (value: string) => {
    if (config.multi) {
      const next = selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value];
      setField(config.field, next, true);
    } else {
      setField(config.field, selected.includes(value) ? [] : [value], false);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 rounded-pill border px-3 py-1.5 text-[13px] font-semibold transition-colors duration-150',
            selected.length > 0
              ? 'border-indigo bg-indigo-tint text-indigo'
              : 'border-hairline text-ink-2 hover:text-ink',
          )}
        >
          <Icon className="size-3.5" />
          {config.label}
          {selected.length > 0 && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-pill bg-indigo px-1 text-[11px] font-semibold text-white">
              {selected.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60">
        <div className="px-1 pb-1 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-ink-2">
          {config.label}
        </div>
        <div className="max-h-64 space-y-0.5 overflow-y-auto">
          {config.options.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex cursor-pointer items-center gap-2.5 rounded-latio-sm px-2 py-1.5 text-sm hover:bg-surface-2"
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(opt.value)} />
                <span className="text-ink">{opt.label}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Row of filter buttons. */
export function Filters({ fields }: { fields: FilterFieldConfig[] }) {
  return (
    <>
      {fields.map((f) => (
        <FilterPopover key={f.field} config={f} />
      ))}
    </>
  );
}

function Token({
  label,
  onRemove,
  tone = 'indigo',
}: {
  label: React.ReactNode;
  onRemove: () => void;
  tone?: 'indigo' | 'neutral';
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill py-1 pl-2.5 pr-1.5 text-[13px] font-medium',
        tone === 'indigo' ? 'bg-indigo-tint text-indigo' : 'bg-surface-2 text-ink-2',
      )}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove"
        className="flex size-4 items-center justify-center rounded-pill hover:bg-black/5"
      >
        <X className="size-3" />
      </button>
    </span>
  );
}

/**
 * Active filter tokens (indigo) + active sort token (neutral) + "Clear all".
 * `fields` is the same config used by <Filters/>, for value→label mapping.
 */
export function FilterTokens({
  fields,
  showSort = true,
  sortLabels,
}: {
  fields: FilterFieldConfig[];
  showSort?: boolean;
  /** Map a RA sort field to a friendly label. */
  sortLabels?: Record<string, string>;
}) {
  const { filterValues, setField } = useFilterState();
  const { sort, setSort, setFilters, displayedFilters } = useListContext();

  const tokens: React.ReactNode[] = [];
  fields.forEach((f) => {
    const selected = asArray(filterValues?.[f.field]);
    selected.forEach((value) => {
      const opt = f.options.find((o) => o.value === value);
      tokens.push(
        <Token
          key={`${f.field}:${value}`}
          label={
            <>
              <span className="opacity-70">{f.label}:</span> {opt?.label ?? value}
            </>
          }
          onRemove={() => setField(f.field, selected.filter((v) => v !== value), f.multi)}
        />,
      );
    });
  });

  const hasFilters = tokens.length > 0;
  const sortToken =
    showSort && sort?.field ? (
      <Token
        key="__sort"
        tone="neutral"
        label={
          <>
            <span className="opacity-70">Sort:</span>{' '}
            {(sortLabels && sortLabels[sort.field]) ?? sort.field} ({sort.order === 'ASC' ? '↑' : '↓'})
          </>
        }
        onRemove={() => setSort({ field: 'created_at', order: 'DESC' })}
      />
    ) : null;

  if (!hasFilters && !sortToken) return null;

  const clearAll = () => {
    const cleared = { ...filterValues };
    fields.forEach((f) => delete cleared[f.field]);
    setFilters(cleared, displayedFilters);
  };

  return (
    <>
      {tokens}
      {sortToken}
      {hasFilters && (
        <button
          type="button"
          onClick={clearAll}
          className="text-[13px] font-semibold text-ink-2 underline-offset-2 hover:text-ink hover:underline"
        >
          Clear all
        </button>
      )}
    </>
  );
}
